import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Edit2, Sliders, Send, Boxes, AlertTriangle, TrendingDown,
  Package, ChevronRight, Truck, FileText, Trash2, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STATUS_META, MOVEMENT_META, UNITS, inr, fmtQty } from "./lib";
import PartFormDialog from "./components/PartFormDialog";
import StockAdjustDialog from "./components/StockAdjustDialog";
import EnquiryDialog from "./components/EnquiryDialog";

const unitLabel = (k) => (UNITS.find((u) => u.key === k) || { label: k || "" }).label;

export default function PartDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("movements");
  const [editOpen, setEditOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/spares/parts/${id}`);
      setPart(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load part");
      nav("/spare-parts/stock");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const deactivate = async () => {
    if (!window.confirm("Deactivate this part? It will be hidden from active lists but history is preserved.")) return;
    try {
      await api.delete(`/spares/parts/${id}`);
      toast.success("Part deactivated");
      nav("/spare-parts/stock");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not deactivate"); }
  };

  if (loading || !part) {
    return <div className="py-10 text-center text-[13px] text-[#667085]">Loading part…</div>;
  }

  const meta = STATUS_META[part.status] || STATUS_META.in_stock;
  const movements = part.recent_movements || [];
  const jobUsage = part.job_card_usage || [];

  return (
    <div className="space-y-4" data-testid="spare-part-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Link to="/spare-parts/stock" data-testid="spare-detail-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">{part.name}</h1>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}
                    data-testid="spare-detail-status">
                {meta.label}
              </span>
            </div>
            <div className="text-[12px] text-[#667085] mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
              {part.part_number && <span className="font-mono">#{part.part_number}</span>}
              {part.brand && <span>{part.brand}</span>}
              {part.category && <span>· {part.category}</span>}
              {part.storage_location && <span>· Rack {part.storage_location}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setEnquiryOpen(true)}
                  data-testid="spare-detail-enquiry"
                  className="h-10 px-3 rounded-md bg-[#1d3557] hover:bg-[#152941] text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
            <Send size={13} /> Submit Enquiry
          </button>
          <button onClick={() => setAdjustOpen(true)}
                  data-testid="spare-detail-adjust"
                  className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
            <Sliders size={13} /> Adjust
          </button>
          <button onClick={() => setEditOpen(true)}
                  data-testid="spare-detail-edit"
                  className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
            <Edit2 size={13} /> Edit
          </button>
          {part.active !== false && (
            <button onClick={deactivate}
                    data-testid="spare-detail-deactivate"
                    className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#e63946] hover:bg-[#e63946]/10 inline-flex items-center gap-1.5">
              <Trash2 size={13} /> Deactivate
            </button>
          )}
        </div>
      </div>

      {/* Stock KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StockKpi label="On Hand" value={fmtQty(part.stock_qty, unitLabel(part.unit))} tone="steel" icon={Boxes} />
        <StockKpi label="Reserved" value={fmtQty(part.reserved_qty || 0, unitLabel(part.unit))} tone="amber" icon={TrendingDown} />
        <StockKpi label="Available" value={fmtQty(part.available_qty, unitLabel(part.unit))} tone={part.available_qty > 0 ? "green" : "red"} icon={Package} highlight />
        <StockKpi label="Reorder at" value={part.reorder_level ? fmtQty(part.reorder_level, unitLabel(part.unit)) : "—"} tone="steel" icon={AlertTriangle} />
      </div>

      {/* Specs + Vendor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <section className="lg:col-span-2 bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="spare-detail-specs">
          <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Part details</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 text-[12.5px]">
            <Info label="Purchase price" value={inr(part.purchase_price)} mono />
            <Info label="Selling price" value={inr(part.selling_price)} mono />
            <Info label="GST" value={part.gst_rate != null ? `${part.gst_rate}%` : "—"} mono />
            <Info label="HSN code" value={part.hsn_code || "—"} mono />
            <Info label="Alternate P/N" value={part.alternate_part_number || "—"} mono />
            <Info label="Max stock level" value={part.max_stock_level ? fmtQty(part.max_stock_level, unitLabel(part.unit)) : "—"} mono />
            <Info label="Vehicle make" value={part.vehicle_make || "—"} />
            <Info label="Vehicle model" value={part.vehicle_model || "—"} />
            <Info label="Variant" value={part.variant || "—"} />
          </div>
          {part.notes && (
            <div className="mt-3 rounded-md bg-[#F1FAEE] border border-[#a8dadc]/30 px-3 py-2 text-[12px] text-[#1d3557]">
              <div className="font-bold text-[10.5px] uppercase tracking-wider text-[#457b9d] mb-1">Notes</div>
              {part.notes}
            </div>
          )}
        </section>
        <VendorCard vendorId={part.preferred_vendor_id} />
      </div>

      {/* Tabs — Movements + Job Card Usage */}
      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        <div className="border-b border-[#E4E7EC] px-2 flex items-center gap-1">
          <TabBtn active={tab === "movements"} onClick={() => setTab("movements")}
                  testId="spare-detail-tab-movements">
            Movements <span className="ml-1 text-[10.5px] text-[#667085]">({movements.length})</span>
          </TabBtn>
          <TabBtn active={tab === "usage"} onClick={() => setTab("usage")}
                  testId="spare-detail-tab-usage">
            Job Card Usage <span className="ml-1 text-[10.5px] text-[#667085]">({jobUsage.length})</span>
          </TabBtn>
        </div>
        <div className="p-4">
          {tab === "movements" ? (
            movements.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-[#667085]">
                No movements yet. Adjust stock or receive inward once purchases are enabled.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]" data-testid="spare-movements-table">
                  <thead>
                    <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                      <th className="px-2 py-2">Date</th>
                      <th className="px-2 py-2">Type</th>
                      <th className="px-2 py-2 text-right">In</th>
                      <th className="px-2 py-2 text-right">Out</th>
                      <th className="px-2 py-2 text-right">Balance</th>
                      <th className="px-2 py-2">Ref</th>
                      <th className="px-2 py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((m) => {
                      const mm = MOVEMENT_META[m.type] || { label: m.type, accent: "#457b9d" };
                      return (
                        <tr key={m.id} className="border-b border-[#F1FAEE] last:border-0" data-testid={`spare-movement-${m.id}`}>
                          <td className="px-2 py-2 text-[#667085]">{new Date(m.at).toLocaleString()}</td>
                          <td className="px-2 py-2">
                            <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: mm.accent }}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: mm.accent }} />
                              {mm.label}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-[#0F9256]">{m.qty_in ? `+${m.qty_in}` : ""}</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-[#e63946]">{m.qty_out ? `−${m.qty_out}` : ""}</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums font-bold">{m.balance}</td>
                          <td className="px-2 py-2 text-[#667085] font-mono text-[11px]">{m.ref_number || m.ref_type || "—"}</td>
                          <td className="px-2 py-2 text-[#667085] max-w-[240px] truncate" title={m.notes}>{m.notes || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            jobUsage.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-[#667085]">
                No active job cards are asking for this part right now.
              </div>
            ) : (
              <ul className="divide-y divide-[#F1FAEE]" data-testid="spare-usage-list">
                {jobUsage.map((jc) => (
                  <li key={jc.id} className="py-2.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <Link to={`/job-cards/${jc.id}`} className="text-[13px] font-mono font-semibold text-[#1d3557] hover:text-[#e63946]">
                        {jc.number || jc.job_number || "—"}
                      </Link>
                      <div className="text-[11.5px] text-[#667085]">
                        {jc.customer_snapshot?.name || "—"} · {jc.vehicle_snapshot?.reg_number || "—"} · {String(jc.status).replace(/_/g, " ")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] text-[#667085] uppercase tracking-wider font-semibold">Requested</div>
                      <div className="text-[14px] font-bold font-mono">{jc.parts_needed?.[0]?.qty ?? 1}</div>
                    </div>
                    <Link to={`/job-cards/${jc.id}?tab=parts`} className="text-[#a8dadc] hover:text-[#e63946]">
                      <ChevronRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      </section>

      <PartFormDialog
        open={editOpen} initial={part}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); }}
      />
      <StockAdjustDialog
        open={adjustOpen} part={part}
        onClose={() => setAdjustOpen(false)}
        onSaved={() => { setAdjustOpen(false); load(); }}
      />
      <EnquiryDialog
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        onSubmitted={() => setEnquiryOpen(false)}
        initialItems={[{
          part_name: part.name,
          part_number: part.part_number || "",
          brand: part.brand || "",
          spec: [part.vehicle_make, part.vehicle_model, part.variant].filter(Boolean).join(" "),
          qty: 1, notes: "",
        }]}
        contextLabel={`For ${part.name}${part.part_number ? ` (${part.part_number})` : ""}`}
      />
    </div>
  );
}

function StockKpi({ icon: Icon, label, value, tone, highlight }) {
  const toneMap = {
    steel: "bg-white border-[#E4E7EC]",
    green: "bg-[#F1FAEE] border-[#12B76A]/25 text-[#0F9256]",
    amber: "bg-[#FEF0C7] border-[#F79009]/25 text-[#B54708]",
    red:   "bg-[#e63946]/8 border-[#e63946]/25 text-[#e63946]",
  };
  return (
    <div className={`rounded-xl border p-3.5 ${toneMap[tone]} ${highlight ? "ring-1 ring-[#1d3557]/10" : ""}`}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-white/70 grid place-items-center text-[#457b9d]">
          <Icon size={13} />
        </div>
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      </div>
      <div className={`text-[22px] font-bold font-mono tabular-nums mt-1.5 ${highlight ? "text-[#1d3557]" : ""}`}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testId}
      className={`h-11 px-3 text-[12.5px] font-semibold border-b-2 ${
        active ? "text-[#1d3557] border-[#e63946]" : "text-[#667085] border-transparent hover:text-[#1d3557]"
      }`}
    >
      {children}
    </button>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#667085] font-semibold">{label}</div>
      <div className={`text-[13px] text-[#1d3557] ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
    </div>
  );
}

function VendorCard({ vendorId }) {
  const [vendor, setVendor] = useState(null);
  useEffect(() => {
    if (!vendorId) { setVendor(null); return; }
    api.get(`/spares/vendors/${vendorId}`).then((r) => setVendor(r.data)).catch(() => setVendor(null));
  }, [vendorId]);
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="spare-detail-vendor">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md bg-[#a8dadc]/30 grid place-items-center text-[#457b9d]">
          <Truck size={14} />
        </div>
        <h3 className="text-[13px] font-bold text-[#1d3557]">Preferred vendor</h3>
      </div>
      {!vendorId ? (
        <div className="text-[12px] text-[#667085]">Not set. Edit part to link a vendor.</div>
      ) : !vendor ? (
        <div className="text-[12px] text-[#667085]">Loading…</div>
      ) : (
        <div className="text-[12.5px] space-y-1">
          <Link to={`/spare-parts/vendors/${vendor.id}`} className="text-[14px] font-bold text-[#1d3557] hover:text-[#e63946]">
            {vendor.name}
          </Link>
          {vendor.contact_person && <div className="text-[#667085]">{vendor.contact_person}</div>}
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[#667085]">
            {vendor.phone && <span>📞 {vendor.phone}</span>}
            {vendor.email && <span>{vendor.email}</span>}
          </div>
          {vendor.gstin && <div className="text-[11px] font-mono text-[#667085]">GST: {vendor.gstin}</div>}
        </div>
      )}
    </section>
  );
}
